import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, 
  Select, MenuItem, TextField, Chip, CircularProgress, Alert, 
  Button, Menu, Divider, Typography, IconButton, FormControl, InputLabel, 
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, Tooltip
} from '@mui/material';
import { 
  ArrowDropDown, Save as SaveIcon, FilterList as FilterIcon, Clear as ClearIcon, 
  Delete as DeleteIcon, Add as AddIcon, Restore as RestoreIcon, Lock as LockIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { questionnaireService } from '../../services/questionnaireService';
import { MaterialItem, ScopeAndMaterialPayload } from '../../types/scopeAndMaterial';

interface MaterialScheduleProps { projectId: string; isReadOnly?: boolean; }

const inferStage = (phaseName: string): string => {
  const phase = (phaseName || '').toLowerCase();
  if (phase.includes('first fix') || phase.includes('structural') || phase.includes('ground') || phase.includes('envelope')) return 'First_Fix';
  if (phase.includes('second fix') || phase.includes('kitchen') || phase.includes('bathroom')) return 'Second_Fix';
  return 'First_Fix';
};

const MaterialSchedule: React.FC<MaterialScheduleProps> = ({ projectId, isReadOnly = false }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [originalMaterials, setOriginalMaterials] = useState<MaterialItem[]>([]);
  const [materials, setMaterials] = useState<MaterialItem[]>([]);
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [filterPhase, setFilterPhase] = useState<string>('All');
  const [filterStage, setFilterStage] = useState<string>('All');
  const [filterSupplier, setFilterSupplier] = useState<string>('All');
  const [sortBy, setSortBy] = useState<string>('phase');
  const [newItem, setNewItem] = useState<Partial<MaterialItem>>({
    phase_name: '', item_category: 'General', item_name: '', quantity_estimated: '', 
    specification_notes: '', supply_status: 'To Be Confirmed', fix_stage: 'First_Fix'
  });

  useEffect(() => {
      const loadMaterials = async () => {
        try {
          const data: ScopeAndMaterialPayload = await questionnaireService.getScopeAndMaterials(projectId);
          if (data) {
              // 1. Process Original AI Data
              const aiMaterials = (data.material_schedule || []).map((item, index) => ({
                  ...item,
                  id: item.id || `mat-${index}`,
                  fix_stage: (item.fix_stage || inferStage(item.phase_name)) as any
              }));
              setOriginalMaterials(aiMaterials);

              // 2. Determine Active Data
              if (data.verified_material_schedule !== undefined && data.verified_material_schedule !== null) {
                  setMaterials(data.verified_material_schedule);
              } else {
                  setMaterials(aiMaterials);
              }
          }
        } catch (error) {
          console.error("Failed to load materials", error);
        } finally {
          setLoading(false);
        }
      };
      loadMaterials();
    }, [projectId]);

  const updateItem = (id: string, field: string, value: any) => {
    if (isReadOnly) return;
    setMaterials(prev => prev.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const handleDelete = (id: string) => {
    if (isReadOnly) return;
    if (window.confirm("Are you sure you want to remove this item?")) {
      setMaterials(prev => prev.filter(item => item.id !== id));
    }
  };

  const handleAddItem = () => {
    if (isReadOnly) return;
    if (!newItem.item_name) return;
    const itemToAdd: MaterialItem = {
      id: `new-${Date.now()}`,
      phase_name: newItem.phase_name || 'General',
      item_category: newItem.item_category || 'General',
      item_name: newItem.item_name!,
      quantity_estimated: newItem.quantity_estimated || '1',
      specification_notes: newItem.specification_notes || '',
      supply_status: newItem.supply_status || 'To Be Confirmed',
      fix_stage: (newItem.fix_stage || inferStage(newItem.phase_name || '')) as any,
      confidence_level: 'High'
    };
    setMaterials(prev => [...prev, itemToAdd]);
    setIsAddDialogOpen(false);
    setNewItem({ phase_name: '', item_category: 'General', item_name: '', quantity_estimated: '', specification_notes: '', supply_status: 'To Be Confirmed', fix_stage: 'First_Fix' });
  };

  const handleSave = async () => {
      if (isReadOnly) return;
      setSaving(true);
      try {
          await questionnaireService.saveScopeAndMaterials(projectId, {
              verified_material_schedule: materials
          });
          alert("Materials saved successfully!");
      } catch (error) {
          console.error("Save failed", error);
          alert("Failed to save. Please check your connection.");
      } finally {
          setSaving(false);
      }
  };

  const handleReset = () => {
    if (isReadOnly) return;
    if (window.confirm("This will discard all your edits and revert to the original AI suggestions. Are you sure?")) {
      setMaterials(JSON.parse(JSON.stringify(originalMaterials)));
    }
  };

  const handleBulkClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isReadOnly) return;
    setAnchorEl(event.currentTarget);
  };
  
  const applyBulkUpdate = (actionType: string) => {
    if (isReadOnly) return;
    setMaterials(prev => prev.map(item => {
        if (actionType === 'ALL_BUILDER') return { ...item, supply_status: 'Builder' };
        if (actionType === 'ALL_CLIENT') return { ...item, supply_status: 'Client' };
        if (actionType === 'SMART_FIRST_FIX' && item.fix_stage === 'First_Fix') return { ...item, supply_status: 'Builder' };
        if (actionType === 'SMART_SECOND_FIX' && item.fix_stage === 'Second_Fix') return { ...item, supply_status: 'Client' };
        return item;
    }));
    setAnchorEl(null);
  };

  const filteredMaterials = useMemo(() => {
    return materials.filter(item => {
      if (filterPhase !== 'All' && item.phase_name !== filterPhase) return false;
      if (filterStage !== 'All' && item.fix_stage !== filterStage) return false;
      if (filterSupplier !== 'All' && (item.supply_status || 'To Be Confirmed') !== filterSupplier) return false;
      return true;
    }).sort((a, b) => {
      if (sortBy === 'phase') return (a.phase_name || '').localeCompare(b.phase_name || '');
      if (sortBy === 'category') return (a.item_category || '').localeCompare(b.item_category || '');
      return 0;
    });
  }, [materials, filterPhase, filterStage, filterSupplier, sortBy]);

  const uniquePhases = Array.from(new Set(materials.map(m => m.phase_name).filter(Boolean))).sort();

  // VALIDATION: Check for unassigned items
  const unassignedCount = materials.filter(m => !m.supply_status || m.supply_status === 'To Be Confirmed').length;
  const hasUnassignedItems = unassignedCount > 0;

  if (loading) return <CircularProgress />;

  return (
    <Box>
      <Alert severity={isReadOnly ? "warning" : "info"} sx={{ mb: 3 }} icon={isReadOnly ? <LockIcon /> : undefined}>
        {isReadOnly 
          ? <strong>Material Schedule Locked.</strong> 
          : <strong>Verify & Edit:</strong>}
        {isReadOnly 
          ? " The material schedule has been finalized. Changes are no longer permitted."
          : " You can rename items, change quantities, or add missing materials below."}
      </Alert>

      {/* WARNING if unassigned items exist */}
      {!isReadOnly && hasUnassignedItems && (
        <Alert severity="warning" sx={{ mb: 3 }} icon={<WarningIcon />}>
          <strong>Action Required:</strong> You have {unassignedCount} item(s) with supplier "To Be Confirmed". 
          You must assign a supplier (Builder or Client) to all items before saving.
        </Alert>
      )}

      {/* --- CONTROL BAR --- */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} lg={8} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Phase</InputLabel>
              <Select value={filterPhase} label="Phase" onChange={(e) => setFilterPhase(e.target.value)}>
                <MenuItem value="All">All Phases</MenuItem>
                {uniquePhases.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Stage</InputLabel>
              <Select value={filterStage} label="Stage" onChange={(e) => setFilterStage(e.target.value)}>
                <MenuItem value="All">All Stages</MenuItem>
                <MenuItem value="First_Fix">First Fix</MenuItem>
                <MenuItem value="Second_Fix">Second Fix</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Supplier</InputLabel>
              <Select value={filterSupplier} label="Supplier" onChange={(e) => setFilterSupplier(e.target.value)}>
                <MenuItem value="All">All Items</MenuItem>
                <MenuItem value="To Be Confirmed">To Be Confirmed</MenuItem>
                <MenuItem value="Builder">Builder Supply</MenuItem>
                <MenuItem value="Client">Client Supply</MenuItem>
              </Select>
            </FormControl>
            {(filterPhase !== 'All' || filterStage !== 'All' || filterSupplier !== 'All') && (
              <Button startIcon={<ClearIcon />} size="small" onClick={() => { setFilterPhase('All'); setFilterStage('All'); setFilterSupplier('All'); }}>Reset</Button>
            )}
          </Grid>

          <Grid item xs={12} lg={4} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
             <Button 
                startIcon={<AddIcon />} 
                variant="contained" 
                size="small" 
                onClick={() => setIsAddDialogOpen(true)}
                disabled={isReadOnly}
            >
                Add Item
            </Button>
             <Button 
                endIcon={<ArrowDropDown />} 
                variant="outlined" 
                color="secondary" 
                size="small" 
                onClick={handleBulkClick}
                disabled={isReadOnly}
            >
                Smart Assign
            </Button>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                <MenuItem onClick={() => applyBulkUpdate('SMART_FIRST_FIX')}>Set <strong>First Fix</strong> → Builder</MenuItem>
                <MenuItem onClick={() => applyBulkUpdate('SMART_SECOND_FIX')}>Set <strong>Second Fix</strong> → Client</MenuItem>
                <Divider />
                <MenuItem onClick={() => applyBulkUpdate('ALL_BUILDER')}>Set ALL to Builder</MenuItem>
                <MenuItem onClick={() => applyBulkUpdate('ALL_CLIENT')}>Set ALL to Client</MenuItem>
            </Menu>
          </Grid>
        </Grid>
      </Paper>

      {/* --- TABLE --- */}
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 650 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }} width="25%">Item Name</TableCell>
              <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }} width="15%">Phase / Stage</TableCell>
              <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }} width="10%">Qty</TableCell>
              <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }} width="30%">Specification</TableCell>
              <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }} width="15%">Supplier</TableCell>
              <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }} width="5%"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredMaterials.length === 0 ? (
               <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>No materials match your filters.</TableCell></TableRow>
            ) : (
              filteredMaterials.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <TextField 
                        fullWidth 
                        size="small" 
                        value={row.item_name} 
                        onChange={(e) => updateItem(row.id!, 'item_name', e.target.value)} 
                        variant="standard" 
                        InputProps={{ disableUnderline: true, sx: { fontSize: '0.875rem', fontWeight: 500 }, readOnly: isReadOnly }} 
                        disabled={isReadOnly}
                        sx={{ '&:hover': { bgcolor: isReadOnly ? 'transparent' : 'grey.50' }, borderRadius: 1, p: 0.5 }} 
                    />
                    <Chip label={row.item_category} size="small" variant="outlined" sx={{ mt: 0.5, fontSize: '0.65rem', height: 20 }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" display="block">{row.phase_name}</Typography>
                    {row.fix_stage === 'First_Fix' && <Chip label="1st Fix" size="small" color="default" sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#e0e0e0' }} />}
                    {row.fix_stage === 'Second_Fix' && <Chip label="2nd Fix" size="small" color="info" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />}
                  </TableCell>
                  <TableCell>
                    <TextField 
                        fullWidth 
                        size="small" 
                        value={row.quantity_estimated} 
                        onChange={(e) => updateItem(row.id!, 'quantity_estimated', e.target.value)} 
                        variant="standard" 
                        InputProps={{ disableUnderline: true, sx: { fontSize: '0.875rem' }, readOnly: isReadOnly }} 
                        disabled={isReadOnly}
                        sx={{ '&:hover': { bgcolor: isReadOnly ? 'transparent' : 'grey.50' }, borderRadius: 1, p: 0.5 }} 
                    />
                  </TableCell>
                  <TableCell>
                    <TextField 
                        fullWidth 
                        size="small" 
                        multiline 
                        value={row.specification_notes} 
                        onChange={(e) => updateItem(row.id!, 'specification_notes', e.target.value)} 
                        variant="standard" 
                        placeholder={isReadOnly ? "" : "Add details..."} 
                        InputProps={{ disableUnderline: true, sx: { fontSize: '0.875rem' }, readOnly: isReadOnly }} 
                        disabled={isReadOnly}
                        sx={{ '&:hover': { bgcolor: isReadOnly ? 'transparent' : 'grey.50' }, borderRadius: 1, p: 0.5 }} 
                    />
                  </TableCell>
                  <TableCell>
                    <Select 
                        fullWidth 
                        size="small" 
                        value={row.supply_status || 'To Be Confirmed'} 
                        onChange={(e) => updateItem(row.id!, 'supply_status', e.target.value)} 
                        disabled={isReadOnly}
                        sx={{ 
                            fontSize: '0.8rem', 
                            height: 32, 
                            bgcolor: row.supply_status === 'Client' ? '#fff3e0' : row.supply_status === 'Builder' ? '#e8f5e9' : '#ffebee', 
                            color: row.supply_status === 'To Be Confirmed' ? 'error.main' : 'text.primary',
                            opacity: isReadOnly ? 0.7 : 1
                        }}
                    >
                      <MenuItem value="To Be Confirmed">❓ Select...</MenuItem>
                      <MenuItem value="Builder">👷 Builder</MenuItem>
                      <MenuItem value="Client">👤 Client</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell>
                    {!isReadOnly && (
                        <Tooltip title="Remove Item">
                            <IconButton size="small" color="error" onClick={() => handleDelete(row.id!)}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button 
            variant="outlined" 
            color="warning" 
            startIcon={<RestoreIcon />} 
            onClick={handleReset} 
            disabled={saving || isReadOnly}
        >
            Reset to AI Default
        </Button>
        <Button 
            variant="contained" 
            size="large" 
            startIcon={saving ? <CircularProgress size={20} color="inherit"/> : <SaveIcon />} 
            onClick={handleSave} 
            disabled={saving || isReadOnly || hasUnassignedItems}
        >
            {saving ? 'Saving...' : 'Confirm & Save Materials'}
        </Button>
      </Box>

      {/* --- ADD ITEM DIALOG --- */}
      <Dialog open={isAddDialogOpen} onClose={() => setIsAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Material Item</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Item Name" fullWidth value={newItem.item_name} onChange={(e) => setNewItem({...newItem, item_name: e.target.value})} autoFocus />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Phase</InputLabel>
                  <Select value={newItem.phase_name || ''} label="Phase" onChange={(e) => setNewItem({...newItem, phase_name: e.target.value})}>
                    {Array.from(new Set(originalMaterials.map(m => m.phase_name))).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Fix Stage</InputLabel>
                  <Select value={newItem.fix_stage || 'First_Fix'} label="Fix Stage" onChange={(e) => setNewItem({...newItem, fix_stage: e.target.value as any})}>
                    <MenuItem value="First_Fix">First Fix (Structural)</MenuItem>
                    <MenuItem value="Second_Fix">Second Fix (Finishes)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={6}><TextField label="Category" fullWidth value={newItem.item_category} onChange={(e) => setNewItem({...newItem, item_category: e.target.value})} /></Grid>
              <Grid item xs={6}><TextField label="Quantity" fullWidth value={newItem.quantity_estimated} onChange={(e) => setNewItem({...newItem, quantity_estimated: e.target.value})} /></Grid>
            </Grid>
            <TextField label="Specification Notes" fullWidth multiline rows={2} value={newItem.specification_notes} onChange={(e) => setNewItem({...newItem, specification_notes: e.target.value})} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddItem} variant="contained" disabled={!newItem.item_name}>Add Item</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MaterialSchedule;